import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'

const MAX_DURATION_SECONDS = 10

const RecordingPage: React.FC = () => {
    const [isRecording, setIsRecording] = useState(false)
    const [audioUrl, setAudioUrl] = useState<string | null>(null)
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const countdownRef = useRef<number>()
    const timeoutRef = useRef<number>()
    const chunksRef = useRef<Blob[]>([])

    const clearTimers = () => {
        if (countdownRef.current) {
            window.clearInterval(countdownRef.current)
            countdownRef.current = undefined
        }
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current)
            timeoutRef.current = undefined
        }
    }

    const cleanupStream = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop())
        streamRef.current = null
    }

    const resetRecorderState = () => {
        clearTimers()
        cleanupStream()
        mediaRecorderRef.current = null
        setIsRecording(false)
        setSecondsRemaining(null)
    }

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop()
            }
            resetRecorderState()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl)
            }
        }
    }, [audioUrl])

    const startRecording = async () => {
        if (typeof window === 'undefined' || !('MediaRecorder' in window)) {
            setError('Recording is not supported in this environment.')
            return
        }

        if (!navigator.mediaDevices?.getUserMedia) {
            setError('Recording is not supported in this browser.')
            return
        }

        setError(null)
        setAudioBlob(null)
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl)
            setAudioUrl(null)
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            streamRef.current = stream
            const preferredMimeTypes = [
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/ogg;codecs=opus',
                'audio/ogg',
                'audio/mp4;codecs=aac',
                'audio/mp4',
            ]
            const mimeType =
                typeof MediaRecorder.isTypeSupported === 'function'
                    ? preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type))
                    : undefined

            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
            mediaRecorderRef.current = recorder
            chunksRef.current = []

            recorder.ondataavailable = (event: BlobEvent) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data)
                }
            }

            recorder.onstop = () => {
                const chunk = chunksRef.current
                cleanupStream()
                clearTimers()
                setIsRecording(false)
                setSecondsRemaining(null)

                if (chunk.length === 0) {
                    setError('No audio recorded. Please try again.')
                    return
                }

                const blob = new Blob(chunk, { type: recorder.mimeType || mimeType || 'audio/webm' })
                setAudioBlob(blob)
                const url = URL.createObjectURL(blob)
                setAudioUrl(url)
            }

            recorder.start()
            setIsRecording(true)
            setSecondsRemaining(MAX_DURATION_SECONDS)

            countdownRef.current = window.setInterval(() => {
                setSecondsRemaining((prev) => {
                    if (prev === null) return prev
                    if (prev <= 1) {
                        window.clearInterval(countdownRef.current)
                        countdownRef.current = undefined
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)

            timeoutRef.current = window.setTimeout(() => {
                stopRecording()
            }, MAX_DURATION_SECONDS * 1000)
        } catch (err) {
            console.error(err)
            if (err instanceof DOMException && err.name === 'NotAllowedError') {
                setError('Microphone permission is required to start recording.')
            } else if (err instanceof DOMException && err.name === 'NotFoundError') {
                setError('No audio input device was found. Check your microphone connection.')
            } else if (err instanceof Error && err.message.includes('supported')) {
                setError('Recording format not supported by this browser. Try updating or switching browsers.')
            } else {
                setError('Microphone access was denied or is unavailable.')
            }
            resetRecorderState()
        }
    }

    const stopRecording = () => {
        clearTimers()
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop()
        } else {
            resetRecorderState()
        }
    }

    return (
        <Box sx={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography variant="h4" gutterBottom>
                    Voice Note Prototype
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Record up to 10 seconds of audio. Once captured, the blob will be available for upload to the
                    backend.
                </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Button
                    variant="contained"
                    color={isRecording ? 'error' : 'primary'}
                    onClick={isRecording ? stopRecording : startRecording}
                >
                    {isRecording ? 'Stop recording' : 'Start recording'}
                </Button>
                {isRecording && (
                    <Typography variant="body1" color="text.secondary">
                        {secondsRemaining ?? MAX_DURATION_SECONDS}s left
                    </Typography>
                )}
            </Stack>

            {audioUrl && audioBlob && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Preview & Payload
                    </Typography>
                    <audio controls src={audioUrl} style={{ width: '100%' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Size: {(audioBlob.size / 1024).toFixed(1)} KB · Type: {audioBlob.type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ready for backend upload: add the blob to a FormData instance, e.g. `formData.append('file',
                        audioBlob, 'recording.webm')`.
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

export default RecordingPage
